import { take, call, put } from 'redux-saga/effects'
import * as types from '../../../lib/actionTypes.es6'
import * as actions from './actions'

import {
  createQuote,
  buyPolicy,
  uploadPolicyDocument,
  createDebitOrderz,
  updateQuote,
  changeQuote
} from '../../../lib/requests.es6'
import { GAloggerException } from '../../../lib/monitoring.es6'
import { unAuthorizedNotification } from '../../../lib/access.es6'
import { getAllPoliciesRequest } from '../../PolicyV2CustomerStatus/Redux/actions'

// Retry configuration for transient failures
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000

/**
 * Retry wrapper with exponential backoff
 * Retries only on transient errors (timeout, 503, 504, 408)
 * Fails immediately on client errors (400, 401, 403, 409)
 */
function* retryable(fn, args, maxRetries = MAX_RETRIES) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return yield call(fn, ...args)
    } catch (e) {
      const isNetworkTimeout =
        e.code === 'ECONNABORTED' || e.code === 'ENOTFOUND'
      const isServerTimeout =
        e.response &&
        (e.response.status === 408 ||
          e.response.status === 503 ||
          e.response.status === 504)
      const isRetryable = isNetworkTimeout || isServerTimeout

      if (isRetryable && attempt < maxRetries) {
        const delayMs = RETRY_DELAY_MS * Math.pow(2, attempt - 1)
        console.warn(
          `[uploadPolicyDocument] Transient error (${e.code ||
            (e.response && e.response.status)}). ` +
            `Retry ${attempt}/${maxRetries} after ${delayMs}ms`
        )
        yield new Promise(resolve => setTimeout(resolve, delayMs))
        continue
      }

      console.error(
        `[uploadPolicyDocument] ${
          isRetryable ? 'Max retries reached' : 'Non-retryable error'
        }. ` + `Status: ${e.response && e.response.status}, Code: ${e.code}`
      )
      throw e
    }
  }
}

export function* createQuoteRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_QUOTE_REQUEST)
    try {
      const response = yield call(createQuote, request.payload)
      yield put(actions.createQuoteSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.createQuoteFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* BuyPolicyRequestWatcher() {
  for (;;) {
    const request = yield take(types.BUY_POLICY_REQUEST)
    try {
      const response = yield call(buyPolicy, request.payload)
      yield put(actions.buyPolicySuccess(response.data))
      yield put(getAllPoliciesRequest({ guid: response.data.customer_guid }))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.buyPolicyFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* changeQuoteRequestWatcher() {
  for (;;) {
    const request = yield take(types.CHANGE_QUOTE_REQUEST)
    try {
      const response = yield call(changeQuote, request.payload)
      yield put(actions.changeQuoteSuccess(response.data))
      yield put(actions.buyPolicySuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.changeQuoteFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* amendQuoteRequestWatcher() {
  for (;;) {
    const request = yield take(types.AMEND_QUOTE_REQUEST)
    try {
      const response = yield call(updateQuote, request.payload)
      yield put(actions.amendQuoteSuccess(response.data))
      yield put(actions.buyPolicySuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.amendQuoteFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* UploadPolicyDocumentRequestWatcher() {
  while (true) {
    const { payload } = yield take(types.UPLOAD_POLICY_DCP_DOCUMENT_REQUEST)
    try {
      const response = yield call(retryable, uploadPolicyDocument, [payload])
      yield put(actions.uploadPolicyDcpDocumentSuccess(response.data))
    } catch (e) {
      let errorPayload = 'Document upload failed. Please try again.'
      let errorCode = 'UNKNOWN_ERROR'

      if (e.response) {
        errorPayload =
          (e.response.data && e.response.data.error) ||
          (e.response.data && e.response.data.message) ||
          `Upload failed (${e.response.status})`
        errorCode = `HTTP_${e.response.status}`
        unAuthorizedNotification(e)
      } else if (e.code === 'ECONNABORTED') {
        errorPayload =
          'Upload took too long (timeout). Please check your connection and try again.'
        errorCode = 'TIMEOUT'
      } else if (e.code === 'ENOTFOUND' || e.code === 'ECONNREFUSED') {
        errorPayload =
          'Network error. Please check your internet connection and try again.'
        errorCode = 'NETWORK_ERROR'
      }

      console.error('[uploadPolicyDocument] Upload failed:', {
        errorCode,
        message: errorPayload
      })
      yield put(actions.uploadPolicyDcpDocumentFailed(errorPayload))

      GAloggerException(e, { context: 'document_upload', errorCode, payload })
    }
  }
}

export function* createDebitOrdersDcpRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_DEBIT_ORDERS_DCP_REQUEST)
    try {
      const response = yield call(createDebitOrderz, request.payload)
      yield put(actions.createDebitOrdersDcpSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.createDebitOrdersDcpFailure(errorMessage))
    }
  }
}
